import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormGroupedCheckboxList, FormMultiSelect } from "components/form-fields";
import { GridLayout } from "components/grid";
import * as helpers from "./AssetSelectionHelpers";

export function AssetSelection(
    {
        onChange = () => { },
        conditions = {},
        isNew = false,
        assetOptions,
        isReadOnly,
        loading,
        saving
    }
) {
    const [expanded, setExpanded] = useState({});
    const [currentSelection, setCurrentSelection] = useState({ selectedAssetTypes: {}, selectedSubTypes: {} });
    const [assetTypesIndex, setAssetTypesIndex] = useState({ assetTypeChildren: (_) => [], parentOfSubType: (_) => null });
    const [checkBoxListModel, setCheckBoxListModel] = useState([]);
    const [availableAssetTypes, setAvailableAssetTypes] = useState([]);

    function onAssetCheckBoxGroupExpandCollapse(id) {

        const clonedModel = [
            ...checkBoxListModel
        ]

        const parsedId = helpers.parseId(id);

        setExpanded(prev => ({
            ...prev,
            [parsedId.id]: !prev[parsedId.id]
        }));

        let group = helpers.findChildItem(clonedModel, id, "group");
        group.expanded = !group.expanded;

        setCheckBoxListModel(clonedModel);
    }

    useEffect(() => {
        const selectedAssetTypeIds = conditions?.assetTypes ?? [];
        const selectedAssetSubTypeIds = conditions?.assetSubTypes ?? [];

        const sel = helpers.buildSelection({ assetTypes: selectedAssetTypeIds, subTypes: selectedAssetSubTypeIds });
        const model = helpers.buildGroupCheckBoxModel(availableAssetTypes, sel, assetTypesIndex, {});

        setCurrentSelection(sel);
        setCheckBoxListModel(model);
    }, [availableAssetTypes])

    useEffect(() => {
        const assetTypes = (assetOptions?.assetTypes ?? []).filter(assetType => assetType.publicAllowed)
            .map(assetType => ({
                ...assetType,
                subtypes: (assetType.subtypes?.filter(subType => subType.id != null))
            }));

        let idx = helpers.buildAssetTypesIndex(assetTypes);
        let sel = helpers.makeEmptySelection;

        setAvailableAssetTypes(assetTypes);
        setAssetTypesIndex(idx)
        setCurrentSelection(sel);
    }, [assetOptions]);

    function onAssetCheckBoxListItemSelected(id) {
        const newSelection = helpers.toggleAssetTypeOrSubType(id, assetTypesIndex, currentSelection);
        const model = helpers.buildGroupCheckBoxModel(availableAssetTypes, newSelection, assetTypesIndex, expanded);
        setCurrentSelection(newSelection);
        setCheckBoxListModel(model);
        updateConditions(newSelection);
    }

    function onAssetCheckBoxListGroupSelected(id) {
        let newSelection;

        if (helpers.isAllId(id)) {
            newSelection = helpers.toggleAllAssetTypes(currentSelection, availableAssetTypes);
        } else {
            newSelection = helpers.toggleAssetTypeWithSubTypes(id, assetTypesIndex, currentSelection);
        }

        const model = helpers.buildGroupCheckBoxModel(availableAssetTypes, newSelection, assetTypesIndex, expanded);

        setCurrentSelection(newSelection);
        setCheckBoxListModel(model);
        updateConditions(newSelection);
    }

    function updateConditions(selectedAssetTypes) {

        const parsedSelection = helpers.parseSelected(selectedAssetTypes);

        const updatedConditions = {
            ...conditions,
            assetTypes: parsedSelection.selectedAssetTypes,
            assetSubTypes: parsedSelection.selectedSubTypes
        };

        onChange(updatedConditions);
    }

    const onConditionAdd = (item, name) => {
        const clone = {
            ...conditions
        };

        const currentConditions = clone[name] ? clone[name] : []

        if (!currentConditions.some(c => c === item.id)) {
            clone[name] = [...currentConditions, item.id];
        }

        onChange(clone);
    }

    const onConditionRemove = (item, name, allItems) => {
        const clone = {
            ...conditions
        };

        const removedId = allItems.find(a => a.name === item).id;
        clone[name] = clone[name].filter(c => c !== removedId);

        onChange(clone);
    }

    const mapSelectedValues = (allItems, selectedItems) => {
        if (selectedItems && allItems) {
            return selectedItems
                .map(i => allItems.find(a => a.id === i))
                .filter(i => i);
        }

        return [];
    }

    return (
        <GridLayout numberOfColumns={isNew ? 2 : 1} gap={30}>
            <div>
                <FormMultiSelect
                    id="asset-ownership-select"
                    loading={loading}
                    disabled={isReadOnly || saving}
                    items={assetOptions?.assetOwnerships}
                    onItemSelect={(item) => onConditionAdd(item, "assetOwnerships")}
                    onRemove={(item) => onConditionRemove(item, "assetOwnerships", assetOptions.assetOwnerships)}
                    placeholder="Please select"
                    headingText="Asset ownerships"
                    fill={isNew}
                    selectedValues={mapSelectedValues(assetOptions.assetOwnerships, conditions?.assetOwnerships)}>
                </FormMultiSelect>
            </div>
            <div>
                <FormMultiSelect
                    id="asset-status-select"
                    loading={loading}
                    disabled={isReadOnly || saving}
                    items={assetOptions?.assetStatuses}
                    onItemSelect={(item) => onConditionAdd(item, "assetStatuses")}
                    onRemove={(item) => onConditionRemove(item, "assetStatuses", assetOptions.assetStatuses)}
                    placeholder="Please select"
                    headingText="Asset statuses"
                    fill={isNew}
                    selectedValues={mapSelectedValues(assetOptions.assetStatuses, conditions?.assetStatuses)}>
                </FormMultiSelect>
            </div>
            <div>
                <FormGroupedCheckboxList headingText="Asset types" disabled={isReadOnly || saving} loading={loading}
                    items={checkBoxListModel} groupsSelectable onGroupToggle={onAssetCheckBoxGroupExpandCollapse}
                    onItemSelect={onAssetCheckBoxListItemSelected}
                    onGroupSelect={onAssetCheckBoxListGroupSelected} />

            </div>
        </GridLayout>
       );
}

AssetSelection.propTypes = {
    loading: PropTypes.bool.isRequired,
    saving: PropTypes.bool.isRequired,
    isReadOnly: PropTypes.bool,
    onChange: PropTypes.func,
    conditions: PropTypes.object,
    assetOptions: PropTypes.object,
    isNew: PropTypes.bool,
};